<template>
  <!-- Botón -->
  <a-button v-if="linkOnly" type="link" class="auto-close-popover__link" @click="openModal">
    Configurar cierre de tickets
  </a-button>

  <a-tooltip v-else placement="left">
    <template #title>
      Configura el cierre automático de tickets sin respuesta del contacto ni atención de un
      agente dentro del tiempo de espera.
    </template>
    <a-button block type="text" class="table-cell__actions-btn" @click="openModal">
      <template #icon>
        <SettingOutlined
          :style="{
            fontSize: '16px',
          }"
        />
      </template>
      <p>Configurar cierre de tickets</p>
    </a-button>
  </a-tooltip>

  <!-- Modal principal -->
  <a-modal
    :open="visible"
    width="520px"
    centered
    :keyboard="false"
    :maskClosable="false"
    @cancel="handleCancel"
  >
    <template #title> Configurar cierre de tickets - {{ strategy?.name }} </template>

    <div class="auto-close">
      <div class="auto-close__header">
        <span id="auto-close-label" class="title__strong">
          Cerrar automáticamente tickets sin respuesta ni atención
        </span>
        <a-switch
          v-model:checked="enabled"
          size="small"
          aria-labelledby="auto-close-label"
          :disabled="isLocked"
          @change="handleToggle"
        />
      </div>

      <p class="message--info auto-close__description">
        Activa esta opción para resolver automáticamente los tickets que no reciban respuesta
        del contacto ni atención de un agente dentro del tiempo de espera configurado.
        <strong>El conteo inicia desde la creación de cada ticket.</strong>
      </p>

      <div class="auto-close__time">
        <span>
          Tiempo de espera
          <a-tooltip
            title="Tiempo máximo para que el contacto responda o un agente atienda el ticket antes de que se cierre automáticamente."
          >
            <InfoCircleOutlined style="color: #00000073" />
          </a-tooltip>
          :
        </span>
        <a-select
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          v-model:value="minutes"
          :options="autoCloseTimeOptions"
          :disabled="!enabled || isLocked"
          placeholder="Seleccionar"
          class="auto-close__select"
        />
      </div>

      <a-alert type="warning" show-icon class="auto-close__alert">
        <template #message>
          <strong>Importante:</strong> Después de iniciar la estrategia, esta configuración no
          podrá ser editada ni desactivada.
        </template>
      </a-alert>
    </div>

    <template #footer>
      <a-button @click="handleCancel">Cancelar</a-button>
      <a-button
        type="primary"
        :loading="loading"
        :disabled="!hasChanges || isLocked"
        @click="handleSave"
      >
        Guardar cambios
      </a-button>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, computed, createVNode } from "vue";
import { SettingOutlined, InfoCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons-vue";
import { message, Modal } from "ant-design-vue";
import { useStore } from "vuex";
import { assignDataToStrategy } from "../../services/agents/assignDataToStrategy";
import { StrategyStatus } from "@/app/shared/utils/enums";

const props = defineProps({
  strategy: {
    type: Object,
  },
  linkOnly: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();

const visible = ref(false);
const loading = ref(false);
const enabled = ref(false);
const minutes = ref<number | undefined>(undefined);
const originalEnabled = ref(false);
const originalMinutes = ref<number | undefined>(undefined);

const autoCloseTimeOptions = [1, 3, 6, 12, 24, 48].map((hours) => ({
  value: hours * 60,
  label: hours === 1 ? "1 hora" : `${hours} horas`,
}));

const isLocked = computed(
  () => props.strategy?.strategy_status !== StrategyStatus.PendingToStart
);

const hasChanges = computed(() => {
  if (enabled.value !== originalEnabled.value) return true;
  return enabled.value && minutes.value !== originalMinutes.value;
});

const openModal = () => {
  const savedMinutes = Number(props.strategy?.autoclose_ticket_minutes) || 0;
  originalEnabled.value = savedMinutes > 0;
  originalMinutes.value = savedMinutes || undefined;
  enabled.value = originalEnabled.value;
  minutes.value = originalMinutes.value;
  visible.value = true;
};

const handleToggle = (checked: boolean) => {
  if (checked && !minutes.value) minutes.value = originalMinutes.value || 60;
};

const handleSave = () => {
  Modal.confirm({
    title: "Estás a punto de guardar los cambios realizados, ¿deseas continuar?",
    okText: "Sí, guardar cambios",
    wrapClassName: "auto-close-modal",
    icon: createVNode(QuestionCircleOutlined),
    cancelText: "No, seguir editando",
    centered: true,
    onOk: async () => {
      loading.value = true;

      try {
        // Nunca debe llegar null/undefined al backend: solo 0 o un entero positivo.
        const autocloseTicketMinutes = enabled.value
          ? Math.trunc(Number(minutes.value)) || 0
          : 0;

        await assignDataToStrategy(props.strategy?.id, "assignAutoClose", {
          autoclose_ticket_minutes: autocloseTicketMinutes,
        });
        store.dispatch("fetchAllStrategies");
        message.success("Cambios guardados con éxito.");
        originalEnabled.value = enabled.value;
        originalMinutes.value = minutes.value;
        visible.value = false;
      } catch {
        message.error("Hubo un error al guardar los cambios");
      }

      loading.value = false;
    },
  });
};

const handleCancel = () => {
  if (!hasChanges.value) {
    visible.value = false;
    return;
  }

  Modal.confirm({
    title: "¿Deseas descartar los cambios realizados?",
    content: "Recuerda que, si no guardas los cambios que realizaste, estos serán descartados.",
    okText: "Sí, descartar",
    cancelText: "No, seguir editando",
    centered: true,
    onOk: () => {
      enabled.value = originalEnabled.value;
      minutes.value = originalMinutes.value;
      visible.value = false;
    },
  });
};
</script>

<style lang="sass" scoped>
.auto-close-popover__link
  padding: 0
  height: auto

.title__strong
  font-weight: 600

.message--info
  margin-bottom: 0
  color: $gray-8

.auto-close
  margin-top: 24px
  &__header
    color: #000000E0
    display: flex
    align-items: center
    gap: 8px

  &__description
    color: #000000A6
    font-size: 14px
    margin: 4px 0 16px 0

  &__time
    display: flex
    align-items: center
    gap: 8px
    margin-bottom: 16px

  &__select
    width: 180px

  &__alert
    :deep(.ant-alert-message)
      color: $gray-9
:global(.auto-close-modal .ant-modal-confirm-body)
  align-items: flex-start !important
:global(.auto-close-modal .anticon)
  margin-top: 3px !important
</style>
